import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { BookOpen, Plane, Home, ChevronRight, Scan, BarChart3, Users, Zap, ArrowRight } from 'lucide-react';
import { authApi } from '@/lib/api';
import { toast } from 'sonner';
import { isAuthenticated } from '@/lib/utils';

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function LandingPage() {
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/dashboard');
    }
  }, [navigate]);

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsLoading(true);
    try {
      const response = await authApi.requestMagicLink(email);
      setMagicLinkSent(true);
      toast.success('Magic link sent! Check your email.');
      
      // For demo purposes, if debug_token is returned (mock mode), auto-verify
      if (response.data.debug_token) {
        toast.info('Demo mode: Auto-verifying...');
        setTimeout(() => {
          navigate(`/auth/verify?token=${response.data.debug_token}`);
        }, 1500);
      }
    } catch (error) {
      toast.error('Failed to send magic link');
    } finally {
      setIsLoading(false);
    }
  };

  const modules = [
    {
      icon: BookOpen,
      name: 'Memora Notes',
      description: 'Transform your notebook into a searchable digital archive with AI-powered OCR.',
      color: '#2563EB',
      image: 'https://images.unsplash.com/photo-1632965053624-eea7c66017de?w=600&q=80'
    },
    {
      icon: Plane,
      name: 'Memora Travel Tag',
      description: 'Keep all your travel documents and expenses organized in one smart tag.',
      color: '#0D9488',
      image: 'https://images.unsplash.com/photo-1661693800472-f1998357a8d2?w=600&q=80'
    },
    {
      icon: Home,
      name: 'Memora Home Hub',
      description: 'Family calendar, grocery lists, and medication tracking for everyone.',
      color: '#10B981',
      image: 'https://images.unsplash.com/photo-1591290619618-904f6dd935e3?w=600&q=80'
    }
  ];

  const features = [
    { icon: Scan, title: 'Scan & Go', description: 'One scan activates your artifact instantly' },
    { icon: BarChart3, title: 'Smart Analytics', description: 'Sponsors get real-time engagement metrics' },
    { icon: Users, title: 'Multi-User', description: 'Share artifacts with family or team members' },
    { icon: Zap, title: 'AI Powered', description: 'OCR and smart extraction for your content' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">M</span>
            </div>
            <span className="font-bold text-xl tracking-tight">MEMORA</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <Link to="/sponsor" className="text-slate-600 hover:text-slate-900 transition-colors font-medium" data-testid="sponsor-link">
              Sponsors
            </Link>
            <Button 
              onClick={() => setShowAuth(true)} 
              className="btn-primary"
              data-testid="get-started-btn"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <motion.section 
        className="pt-32 pb-20 px-6"
        initial="initial"
        animate="animate"
        variants={stagger}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div variants={fadeIn}>
              <h1 className="text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight mb-6">
                Turn Physical Artifacts into
                <span className="text-blue-600"> Digital Intelligence</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 max-w-lg">
                MEMORA transforms everyday items—notebooks, travel tags, home hubs—into powerful digital tools with NFC and QR technology.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => setShowAuth(true)} 
                  className="btn-primary flex items-center gap-2"
                  data-testid="hero-cta-btn"
                >
                  Activate Your Artifact
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Link to="/sponsor">
                  <Button variant="outline" className="btn-outline" data-testid="sponsor-portal-btn">
                    Sponsor Portal
                  </Button>
                </Link>
              </div>
            </motion.div>
            
            <motion.div 
              variants={fadeIn}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1632965053624-eea7c66017de?w=800&q=80"
                  alt="Memora Notebook"
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent" />
              </div>
              
              {/* Floating card */}
              <motion.div 
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 border border-slate-100"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Memora Notes</p>
                    <p className="text-sm text-slate-500">18 pages scanned</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Modules Section */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Three Modules, Endless Possibilities
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Each MEMORA artifact is designed for a specific purpose, powered by AI and cloud sync.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {modules.map((module, index) => (
              <motion.div
                key={module.name}
                className="module-card group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`module-card-${module.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="aspect-video relative overflow-hidden">
                  <img 
                    src={module.image} 
                    alt={module.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div 
                    className="absolute bottom-4 left-4 w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: module.color }}
                  >
                    <module.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{module.name}</h3>
                  <p className="text-slate-600 mb-4">{module.description}</p>
                  <button className="flex items-center gap-2 text-slate-900 font-medium group-hover:gap-3 transition-all">
                    Learn more <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="text-center p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-slate-900" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* For Sponsors Section */}
      <section className="py-20 px-6 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-amber-400 font-semibold mb-4 block">FOR CORPORATE SPONSORS</span>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Marketing Analytics for Physical Giveaways
              </h2>
              <p className="text-slate-300 text-lg mb-8">
                Track activation rates, engagement metrics, and ROI for your branded artifacts. See where your products are being used and how often.
              </p>
              <ul className="space-y-4 mb-8">
                {['Real-time activation tracking', 'Geographic heatmaps', 'CTA click analytics', 'Shareable reports'].map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-amber-400 rounded-full flex items-center justify-center">
                      <ChevronRight className="w-4 h-4 text-slate-900" />
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/sponsor">
                <Button className="bg-amber-400 text-slate-900 hover:bg-amber-300 rounded-full px-8 py-3 font-semibold" data-testid="sponsor-cta-btn">
                  Sponsor Portal
                </Button>
              </Link>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1762171696586-e03dabfa81cd?w=800&q=80"
                alt="Sponsor Analytics"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Scan your artifact's QR code or NFC tag to activate it instantly.
          </p>
          <Button 
            onClick={() => setShowAuth(true)}
            className="btn-primary text-lg px-12 py-4"
            data-testid="final-cta-btn"
          >
            Activate Your Artifact
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-slate-100">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">M</span>
            </div>
            <span className="font-bold tracking-tight">MEMORA</span>
          </div>
          <p className="text-slate-500 text-sm">
            © 2026 MEMORA. Transforming physical artifacts into digital intelligence.
          </p>
        </div>
      </footer>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Welcome to MEMORA</DialogTitle>
            <DialogDescription>
              Enter your email to receive a magic link and access your artifacts.
            </DialogDescription>
          </DialogHeader>
          
          {!magicLinkSent ? (
            <form onSubmit={handleMagicLink} className="space-y-4 mt-4">
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl h-12"
                data-testid="email-input"
                required
              />
              <Button 
                type="submit" 
                className="w-full btn-primary"
                disabled={isLoading}
                data-testid="send-magic-link-btn"
              >
                {isLoading ? 'Sending...' : 'Send Magic Link'}
              </Button>
            </form>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ChevronRight className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Check your email!</h3>
              <p className="text-slate-600">We sent a magic link to <strong>{email}</strong></p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
